/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { FC, useRef, ChangeEvent, MouseEvent } from 'react'
import { makeStyles, createStyles, Link } from 'transfix-ui/core'
import uuid from 'uuid'
import { IFile, IUploadComponentProps } from 'apps/fleet-planner/types'
import { filePromises } from 'utils/fileUploadHelper'

interface IUploadInputProps extends IUploadComponentProps {}

const DEFAULT_INPUT_PROPS: IUploadInputProps['InputProps'] = {
  accept: '.png, .jpg, .jpeg, .pdf, .x-pdf',
  multiple: true,
}

type TClassKey = 'input' | 'label' | 'button'

const useUploadInputStyles = makeStyles(
  createStyles<TClassKey, Pick<IUploadInputProps, 'disabled'>>({
    input: {
      display: 'none',
    },
    label: {
      display: 'inline-block',
      cursor: props => (props.disabled ? 'disabled' : 'pointer'),
    },
    button: {
      transition: 'none',
      cursor: props => (props.disabled ? 'disabled' : 'pointer'),
      textDecoration: 'underline',
      color: 'inherit',
    },
  })
)

/**
 * Helper component to show a button for uploading files
 */
export const UploadInputLink: FC<IUploadInputProps> = ({
  disabled = false,
  label = 'Upload',
  InputProps = {},
  onUpload,
}) => {
  const inputProps = { ...DEFAULT_INPUT_PROPS, ...InputProps }

  const id = useRef(uuid())
  const classes = useUploadInputStyles({ disabled })
  const buttonRef = useRef<HTMLSpanElement>(null)

  const changeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e || {}
    const { files } = target || {}

    if (files) {
      const fileList = Array.from(files)
      const packageFiles = await Promise.all(filePromises(fileList))

      const pkgFiles = packageFiles.map(pkg => {
        const pkgFile = pkg as IFile
        pkgFile.uuid = uuid()
        return pkgFile
      })

      if (buttonRef.current) {
        buttonRef.current.blur()
      }

      onUpload(pkgFiles)
    }
  }

  const clickHandler = (e: MouseEvent<HTMLLabelElement>) => {
    if (disabled) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  return (
    <>
      {/**
       * Input is hidden by default.
       * Only element visible is <Button />
       */}
      <input
        {...inputProps}
        className={classes.input}
        id={id.current}
        type='file'
        onChange={changeHandler}
      />
      <label htmlFor={id.current} className={classes.label} onClick={clickHandler}>
        <Link className={classes.button} component='span' ref={buttonRef}>
          {label}
        </Link>
      </label>
    </>
  )
}
